import type { IHttpClient } from "@/core/interfaces";
import { extractItems, isRecord, readErrorDetails, readFirstText } from "@/core/utils";
import { readServiceError } from "@/core/services";
import type { APIError } from "@/core/services";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  AtualizarPeriodoAlocacaoParams,
  DefinirEquipeObraParams,
  DivergenciaConflito,
  DivergenciaIndisponibilidade,
  DivergenciasAlocacao,
  EquipeObraAlocacao,
  EquipeObraCampo,
  EquipeObraDefinida,
  EquipeObraGravada,
  EquipeObraProfissional,
  PeriodoAlocacaoCampo,
} from "./equipe-obra.types";

/** Contrato consumido do hub-bff (HU 2050). */
export const EQUIPE_OBRA_PATHS = {
  DEFINIR: (obraId: string) =>
    `/api/recursos/alocacoes/obras/${encodeURIComponent(obraId)}/profissionais`,
  /** GET da equipe gravada (HU 2051): mesmo caminho do POST. */
  EQUIPE: (obraId: string) =>
    `/api/recursos/alocacoes/obras/${encodeURIComponent(obraId)}/profissionais`,
  ALOCACAO: (obraId: string, alocacaoId: string) =>
    `/api/recursos/alocacoes/obras/${encodeURIComponent(obraId)}/profissionais/${encodeURIComponent(alocacaoId)}`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function toAlocacao(raw: unknown): EquipeObraAlocacao | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    papelObraId: readFirstText(raw, "papelObraId").trim(),
    usuarioId: readFirstText(raw, "usuarioId").trim(),
    inicio: readFirstText(raw, "inicio").trim(),
    fim: readFirstText(raw, "fim").trim(),
  };
}

/**
 * Define a equipe da obra num único POST transacional: ou todas as alocações
 * são gravadas, ou nenhuma.
 */
export async function definirEquipeObra({
  obraId,
  alocacoes,
  justificativa,
  signal,
}: DefinirEquipeObraParams): Promise<EquipeObraDefinida> {
  const response = await getHttpClient().post<unknown>(
    EQUIPE_OBRA_PATHS.DEFINIR(obraId),
    justificativa ? { alocacoes, justificativa } : { alocacoes },
    { signal },
  );

  const body = isRecord(response.data) ? response.data : {};
  const data = isRecord(body.data) ? body.data : body;

  return {
    obraId: readFirstText(data, "obraId").trim() || obraId,
    alocacoes: extractItems(data.alocacoes)
      .map(toAlocacao)
      .filter((alocacao): alocacao is EquipeObraAlocacao => alocacao !== null),
  };
}

function toProfissional(raw: unknown): EquipeObraProfissional | null {
  if (!isRecord(raw)) return null;

  const id = readFirstText(raw, "id").trim();
  if (!id) return null;

  return {
    id,
    papelObraId: readFirstText(raw, "papelObraId").trim(),
    papelNome: readFirstText(raw, "papelNome").trim(),
    papelObrigatorio: raw.papelObrigatorio === true,
    usuarioId: readFirstText(raw, "usuarioId").trim(),
    profissionalNome: readFirstText(raw, "profissionalNome").trim(),
    inicio: readFirstText(raw, "inicio").trim(),
    fim: readFirstText(raw, "fim").trim(),
    updatedAt: readFirstText(raw, "updatedAt").trim(),
  };
}

function unwrap(responseData: unknown): Record<string, unknown> {
  const body = isRecord(responseData) ? responseData : {};
  return isRecord(body.data) ? body.data : body;
}

/** Equipe já gravada da obra: dados da obra e uma linha por alocação (HU 2051). */
export async function listarEquipeObra(
  obraId: string,
  signal?: AbortSignal,
): Promise<EquipeObraGravada> {
  const response = await getHttpClient().get<unknown>(EQUIPE_OBRA_PATHS.EQUIPE(obraId), {
    signal,
  });

  const data = unwrap(response.data);
  const obra = isRecord(data.obra) ? data.obra : {};

  return {
    obra: {
      id: readFirstText(obra, "id").trim() || obraId,
      codigo: readFirstText(obra, "codigo").trim(),
      nome: readFirstText(obra, "nome").trim(),
      embarcacao: isRecord(obra.embarcacao)
        ? { acronimo: readFirstText(obra.embarcacao, "acronimo").trim() || null }
        : null,
      inicio: readFirstText(obra, "inicio").trim(),
      fim: readFirstText(obra, "fim").trim(),
    },
    alocacoes: extractItems(data.alocacoes)
      .map(toProfissional)
      .filter((item): item is EquipeObraProfissional => item !== null),
  };
}

/** Altera somente o período (`inicio`/`fim`) de uma alocação gravada. */
export async function atualizarPeriodoAlocacao({
  obraId,
  alocacaoId,
  inicio,
  fim,
  justificativa,
  ifMatch,
  signal,
}: AtualizarPeriodoAlocacaoParams): Promise<EquipeObraProfissional | null> {
  const response = await getHttpClient().patch<unknown>(
    EQUIPE_OBRA_PATHS.ALOCACAO(obraId, alocacaoId),
    justificativa ? { inicio, fim, justificativa } : { inicio, fim },
    { signal, ...(ifMatch ? { headers: { "If-Match": ifMatch } } : {}) },
  );

  const alocacao = toProfissional(unwrap(response.data));
  const etag = response.headers?.etag?.replace(/^W\//, "").replace(/"/g, "").trim();
  return alocacao && !alocacao.updatedAt && etag ? { ...alocacao, updatedAt: etag } : alocacao;
}

function periodo(raw: unknown): { inicio: string; fim: string } {
  const record = isRecord(raw) ? raw : {};
  return {
    inicio: readFirstText(record, "inicio").trim().slice(0, 10),
    fim: readFirstText(record, "fim").trim().slice(0, 10),
  };
}

/**
 * Divergências de um 409 `DIVERGENCIAS_ALOCACAO` (conflitos e indisponibilidades que o
 * servidor encontrou); `null` se o erro é outro ou não traz nenhuma.
 */
export function readDivergenciasAlocacao(error: APIError): DivergenciasAlocacao | null {
  const { code, details } = readServiceError(error);
  if (code !== "DIVERGENCIAS_ALOCACAO") return null;

  const conflitos = extractItems(details.conflitos).flatMap((raw): DivergenciaConflito[] => {
    if (!isRecord(raw)) return [];
    const obraAtual = isRecord(raw.obraAtual) ? raw.obraAtual : {};
    return [
      {
        usuarioId: readFirstText(raw, "usuarioId").trim(),
        papelObraId: readFirstText(raw, "papelObraId").trim(),
        obraAtual: {
          id: readFirstText(obraAtual, "id").trim(),
          codigo: readFirstText(obraAtual, "codigo").trim(),
          nome: readFirstText(obraAtual, "nome").trim(),
          embarcacao: isRecord(obraAtual.embarcacao)
            ? { acronimo: readFirstText(obraAtual.embarcacao, "acronimo").trim() || null }
            : null,
          ...periodo(obraAtual),
        },
        novaAlocacao: periodo(raw.novaAlocacao),
        coincidencia: periodo(raw.coincidencia),
      },
    ];
  });

  const indisponibilidades = extractItems(details.indisponibilidades).flatMap(
    (raw): DivergenciaIndisponibilidade[] =>
      isRecord(raw)
        ? [
            {
              usuarioId: readFirstText(raw, "usuarioId").trim(),
              papelObraId: readFirstText(raw, "papelObraId").trim(),
              periodoObra: periodo(raw.periodoObra),
              periodoIndisponivel: periodo(raw.periodoIndisponivel),
            },
          ]
        : [],
  );

  return conflitos.length > 0 || indisponibilidades.length > 0
    ? { conflitos, indisponibilidades }
    : null;
}

/** 409 `ALOCACAO_DESATUALIZADA`: outra pessoa alterou a alocação depois da leitura. */
export function isAlocacaoDesatualizada(error: APIError): boolean {
  return error.status === 409 && readServiceError(error).code === "ALOCACAO_DESATUALIZADA";
}

/** Erros por campo (`inicio`/`fim`) de um 400 do PATCH; o resto vai para `general`. */
export function readPeriodoAlocacaoErrors(error: APIError): {
  fields: Partial<Record<PeriodoAlocacaoCampo, string>>;
  general: string[];
} {
  const fields: Partial<Record<PeriodoAlocacaoCampo, string>> = {};
  const general: string[] = [];

  for (const [key, message] of Object.entries(readErrorDetails(error.details))) {
    const field = key.split(".").pop();
    if (field === "inicio" || field === "fim") fields[field] = message;
    else general.push(message);
  }

  return { fields, general };
}

const DETAIL_KEY = /^alocacoes\.(\d+)\.(papelObraId|usuarioId|inicio|fim)$/;

/**
 * Erros por campo de um 400 (`alocacoes.N.campo`): mapa por índice da alocação
 * enviada; chaves que não seguem o padrão ficam em `general`.
 */
export function readEquipeObraErrors(error: APIError): {
  byIndex: Map<number, Partial<Record<EquipeObraCampo, string>>>;
  general: string[];
} {
  const byIndex = new Map<number, Partial<Record<EquipeObraCampo, string>>>();
  const general: string[] = [];

  for (const [key, message] of Object.entries(readErrorDetails(error.details))) {
    const match = DETAIL_KEY.exec(key);
    if (!match) {
      general.push(message);
      continue;
    }

    const index = Number(match[1]);
    byIndex.set(index, { ...byIndex.get(index), [match[2] as EquipeObraCampo]: message });
  }

  return { byIndex, general };
}
